import {ref, computed} from 'vue';
import {useStore} from "vuex";

import {useVuelidate} from '@vuelidate/core';
import {
    required, numeric, bik, checking_account, oktmo, innfl,
    email, minLength, inn,
    ogrn, passport_series, passport_numbers, kpp, snils
} from "@/utils/i18n-validators";
import {useRouter} from "vue-router";

export function useVendor() {
    const store = useStore();
    const errors = ref(null)
    const vendor = computed(() => store.getters.getCurrentVendor);
    const router = useRouter();
    const lang = computed(() => store.getters.getLang);
    const langPostfix = computed(() => lang.value === 'ru' ? '' : `_${lang.value}`);

    const file = ref(null);
    const selectFileMemory = ({files}) => {
        file.value = files[0];
    };

    const deleteFileMemory = ({files}) => {
        file.value = false;
    };

    const form = ref({
        email: '',
        phone: '',
        type: '',
        discount: '',
        account: '',
        corr_account: '',
        bic: '',
        bank: '',
        bank_en: '',
        short_name: '',
        short_name_en: '',
        full_name: '',
        full_name_en: '',
        legal_form: '',
        inn: '',
        kpp: '',
        ogrn: '',
        ogrn_date: '',
        ogrn_place: '',
        ogrn_place_en: '',
        reg_date: '',
        oktmo: '',
        postcode: '',
        region: '',
        region_en: '',
        city: '',
        city_en: '',
        street: '',
        street_en: '',
        house: '',
        building: '',
        corps: '',
        floor: '',
        room: '',
        postcode_fact: '',
        region_fact: '',
        region_fact_en: '',
        city_fact: '',
        city_fact_en: '',
        street_fact: '',
        street_fact_en: '',
        house_fact: '',
        building_fact: '',
        corps_fact: '',
        floor_fact: '',
        room_fact: '',
        passport_issuer_code: '',
        passport_issuer: '',
        passport_date: '',
        passport_series: '',
        passport_number: '',
        snils: '',
        org_name: '',
        ogrnip_place: '',
        ogrnip_date: '',
        ogrnip: '',
        citizenship: '',
        birth_date: '',
        fisrt_name: '',
        last_name: '',
        patronymic: '',
    });

    const mainRulesUl = computed(() => ({
        type: {required},
        [`short_name${langPostfix.value}`]: {required},
        [`full_name${langPostfix.value}`]: {required},
        legal_form: {required},
        inn: {required, inn},
        kpp: {required, kpp},
        ogrn: {required, ogrn},
        ogrn_date: {required},
        ogrn_place: {required},
        [`ogrn_place${langPostfix.value}`]: {required},
        reg_date: {required},
        oktmo: {required},
        postcode: {required, passport_numbers},
        region: {required},
        [`region${langPostfix.value}`]: {required},
        city: {required},
        [`city${langPostfix.value}`]: {required},
        street: {required},
        [`street${langPostfix.value}`]: {required},
        house: {required},
        postcode_fact: {required, passport_numbers},
        region_fact: {required},
        [`region_fact${langPostfix.value}`]: {required},
        city_fact: {required},
        [`city_fact${langPostfix.value}`]: {required},
        street_fact: {required},
        [`street_fact${langPostfix.value}`]: {required},
        house_fact: {required},
        email: {required, email},
        phone: {required},
        discount: {},
    }));

    const mainRulesIP = {
        last_name: {required},
        first_name: {required},
        inn: {required, innfl},
        oktmo: {required},
        ogrnip: {required, ogrn},
        ogrnip_date: {required},
        ogrnip_place: {required},
        passport_issuer: {required},
        passport_issuer_code: {required},
        passport_date: {required},
        passport_series: {required, passport_series},
        passport_number: {required, passport_numbers},
        snils: {required, snils},
        postcode: {required, passport_numbers},
        region: {required},
        city: {required},
        street: {required},
        house: {required},
        postcode_fact: {required, passport_numbers},
        region_fact: {required},
        city_fact: {required},
        street_fact: {required},
        house_fact: {required},
        email: {required, email},
        phone: {required},
    }

    const vip$ = useVuelidate(mainRulesIP, form);
    const v$ = useVuelidate(mainRulesUl, form);

    const isCreate = ref(false);

    const loadVendor = async (id) => {
        await store.dispatch('fetchVendor', id);

    };

    const createVendor = async (type) => {
        errors.value = null
        let result = null;

        if (type === 'ul') result = await v$.value.$validate();
        if (type === 'ip') result = await vip$.value.$validate();
        if (!result) {
            return;
        }
        let formData = {}
        if (type === 'ul') {
            formData = {
                ...form.value,
                ogrn_date: new Date(form.value.ogrn_date).toLocaleDateString('ru-RU', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                }),
                reg_date: new Date(form.value.reg_date).toLocaleDateString('ru-RU', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                }),
            }
        } else {
            formData = {
                ...form.value,
                ogrnip_date: new Date(form.value.ogrnip_date).toLocaleDateString('ru-RU', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                }),
                passport_date: new Date(form.value.reg_date).toLocaleDateString('ru-RU', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                }),
            }
        }
        try {
            await store.dispatch('fetchCreateVendor', formData);
            if (vendor.value.id && file.value) {
                const form = new FormData();
                form.append('payment_details', file.value);
                await store.dispatch('fetchUploadVendorPayment', {
                    id: vendor.value.id,
                    body: form
                });
            }
            await router.push({name: 'vendors-list'})
        } catch (e) {
            errors.value = e.response.data
        }
    };

    return {
        selectFileMemory,
        deleteFileMemory,
        createVendor,
        loadVendor,
        isCreate,
        vendor,
        file,
        form,
        vip$,
        v$,
        errors,
    }
}